import entryKinds from 'data/budget/entryKinds.json'
import type { Scalar } from 'src/tpbp-core/models/global.type'
import type { BudgetItemConfigurationModel } from 'src/tpbp-json-api-handler/models/models'
import { StoreDictionary } from 'src/tpbp-json-api-handler/utils/StoreDictionary/StoreDictionary'
import { sortByNullAfter } from 'src/tpbp-core/utils/common.utils'
import { FEATURE_IDS } from 'src/tpbp-core/feature-flag/featureFlags.const'
import { hasFeature } from 'src/tpbp-core/feature-flag/featureFlags.utils'
import _ from 'underscore'
import type { BudgetAggregateDataByKey } from 'src/tpbp-json-api-handler/budget/Budget.types'

const storeDictionary = new StoreDictionary()

const STORE_KEYS = {
  global: 'global',
  categoriesByType: 'categoriesByType',
  categoriesByEntryCategory: 'categoriesByEntryCategory',
  category: 'category',
  entryKindConfigurationByEntryKind: 'entryKindConfigurationByEntryKind',
  entryCategoriesByProductKind: 'entryCategoriesByProductKind',
  entryKindsByProductKind: 'entryKindsByProductKind',
}

function isInitialized() {
  return storeDictionary.has(STORE_KEYS.global, STORE_KEYS.categoriesByType)
}

function setCategoriesByType(categoriesByType) {
  storeDictionary.set(
    STORE_KEYS.global,
    STORE_KEYS.categoriesByType,
    categoriesByType,
  )

  for (const type of Object.keys(categoriesByType)) {
    const categories = categoriesByType[type]

    for (const category of categories) {
      storeDictionary.set(STORE_KEYS.category, category.key, category)
    }
  }
}

function setCategoriesByEntryCategory(categories) {
  storeDictionary.set(
    STORE_KEYS.global,
    STORE_KEYS.categoriesByEntryCategory,
    categories,
  )
}

function getCategoriesByEntryCategory() {
  return storeDictionary.get<Record<string, BudgetItemConfigurationModel>>(
    STORE_KEYS.global,
    STORE_KEYS.categoriesByEntryCategory,
  )
}

function getCategoriesByType(): BudgetAggregateDataByKey {
  if (!isInitialized()) {
    throw new Error('categoriesByType is not initialized')
  }
  return (
    storeDictionary.get(STORE_KEYS.global, STORE_KEYS.categoriesByType) ?? {}
  )
}

function getCategoryById(key) {
  if (!isInitialized()) {
    throw new Error('categoriesByType is not initialized')
  }
  return storeDictionary.get(STORE_KEYS.category, key)
}

function addToProductKinds(budgetItemsByProductKinds, productKind, value) {
  if (!Object.hasOwn(budgetItemsByProductKinds, productKind)) {
    budgetItemsByProductKinds[productKind] = []
  }
  if (!budgetItemsByProductKinds[productKind].includes(value)) {
    budgetItemsByProductKinds[productKind].push(value)
  }
}

function _getLinkedPortfolioIsPresent(entryKind: string) {
  const configuration = getEntryKindConfiguration(entryKind)

  return configuration?.dependentObjects?.linked_portfolios?.presence ?? false
}

const getLinkedPortfolioIsPresent = _.memoize(_getLinkedPortfolioIsPresent)

function setEntryKindConfigurations(configurations) {
  const entryCategoriesByProductKind = {}
  const entryKindsByProductKind = {}

  for (const configuration of configurations) {
    const {
      entryKind,
      dependentObjects,
      displayedCategory: entryCategory,
    } = configuration

    const productKinds = dependentObjects?.linked_portfolios?.product_kinds
    if (productKinds?.length > 0) {
      for (const productKind of productKinds) {
        addToProductKinds(
          entryCategoriesByProductKind,
          productKind,
          entryCategory,
        )
        addToProductKinds(entryKindsByProductKind, productKind, entryKind)
      }
    }

    storeDictionary.set(
      STORE_KEYS.entryKindConfigurationByEntryKind,
      entryKind,
      configuration,
    )
  }

  for (const productKind of Object.keys(entryCategoriesByProductKind)) {
    const entryCategories = entryCategoriesByProductKind[productKind]
    storeDictionary.set(
      STORE_KEYS.entryCategoriesByProductKind,
      productKind,
      entryCategories,
    )
  }

  for (const productKind of Object.keys(entryKindsByProductKind)) {
    const entryKinds = entryKindsByProductKind[productKind]
    storeDictionary.set(
      STORE_KEYS.entryKindsByProductKind,
      productKind,
      entryKinds,
    )
  }
}

function getEntryCategoriesForProductKind(productKind) {
  if (!productKind) {
    return []
  }
  if (
    !storeDictionary.has(STORE_KEYS.entryCategoriesByProductKind, productKind)
  ) {
    throw new Error(`Unknown entryKind '${productKind}'`)
  }

  return storeDictionary.get(
    STORE_KEYS.entryCategoriesByProductKind,
    productKind,
  )
}

function getEntryKindsForProductKind(productKind) {
  if (!productKind) {
    return []
  }
  if (!storeDictionary.has(STORE_KEYS.entryKindsByProductKind, productKind)) {
    throw new Error(`Unknown entryKind '${productKind}'`)
  }

  return storeDictionary.get(STORE_KEYS.entryKindsByProductKind, productKind)
}

function getEntryKindConfiguration(
  entryKind: Scalar,
): BudgetItemConfigurationModel | null {
  if (!entryKind) return null
  if (
    !storeDictionary.has(
      STORE_KEYS.entryKindConfigurationByEntryKind,
      entryKind,
    )
  ) {
    throw new Error(`Unknown entryKind '${entryKind}'`)
  }

  return storeDictionary.get(
    STORE_KEYS.entryKindConfigurationByEntryKind,
    entryKind,
  )
}

function getCategoriesEntryKinds(entryCategory, ownershipCode) {
  if (!entryCategory) {
    return []
  }
  const key = `${entryCategory}—${ownershipCode}`
  if (!storeDictionary.has('entryKindConfigurationByEntryCategory', key)) {
    const entryKindsByKind = storeDictionary.getTypeSet(
      STORE_KEYS.entryKindConfigurationByEntryKind,
    )

    const entryKindList = Object.values(entryKindsByKind)
      .filter((entryKind: any) => entryKind.displayedCategory === entryCategory)
      .map((entryKind: any) => {
        const staticEntryKind = entryKinds.find(
          (staticEntryKind) =>
            staticEntryKind.entryKind === entryKind.entryKind,
        )
        const order = staticEntryKind ? staticEntryKind.order : null
        const ownershipCodes = entryKind.enums.ownership_code

        const isOk = ownershipCodes.includes(ownershipCode)

        const response = {
          key: entryKind.entryKind,
          label: entryKind.entryKindLabel,
          order,
          isOk,
        }

        const hasFeatureEvolMvp3 = hasFeature(FEATURE_IDS.evolMvp3)
        if (hasFeatureEvolMvp3) {
          const entryKindConfig =
            budgetApiServiceStore.getEntryKindConfiguration(entryKind.entryKind)
          if (!entryKindConfig)
            throw new Error(
              'no entryKindConfig found for ' + entryKind.entryKind,
            )
          const entryKindOrder = entryKindConfig.entryKindOrder
          const hidden = entryKindConfig.hidden
          Object.assign(response, { entryKindOrder, hidden })
        }

        return response
      })
      .filter((row) => row.isOk)

    sortByNullAfter(entryKindList, 'order')

    storeDictionary.set(
      STORE_KEYS.entryKindConfigurationByEntryKind,
      key,
      entryKindList,
    )
  }

  return storeDictionary.get(STORE_KEYS.entryKindConfigurationByEntryKind, key)
}

export const budgetApiServiceStore = {
  isInitialized,
  setCategoriesByType,
  getCategoriesByType,
  setCategoriesByEntryCategory,
  getCategoriesByEntryCategory,
  setEntryKindConfigurations,
  getEntryKindConfiguration,
  getCategoriesEntryKinds,
  getCategoryById,
  getEntryCategoriesForProductKind,
  getEntryKindsForProductKind,
  getLinkedPortfolioIsPresent,
}
